import {
  Avatar,
  Grid,
  HStack,
  Icon,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react";
import { RiAddLine, RiMapPin2Line } from "@remixicon/react";
import { Suspense, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { TitlePageDashboard } from "../../../components/button/TitlePageDashboard";
import { Empty } from "../../../components/card/Empty";
import { BestSellerCard } from "../../../components/dashboard_reseller/BestSellerCard";
import { CardProductMyStore } from "../../../components/dashboard_reseller/CardProductMyStore";
import FilterDrawer from "../../../components/drawer/FilterDrawer";
import SearchInput from "../../../components/input/SearchInput";
import CategoryFilter from "../../../components/menu/CategoryFilter";
import { MenuSort } from "../../../components/menu/MenuSort";
import Pagination from "../../../components/Pagination";
import { CardSkeleton } from "../../../components/skeleton/CardSkeleton";
import { PaginationInterface } from "../../../constants/Pagination";
import {
  CardProductMyStoreInterface,
  ProductInterface,
} from "../../../constants/Product";
import { SelectOption } from "../../../constants/SelectOption";
import { DetailSupplierData } from "../../../constants/Supplier";
import { useAddProduct } from "../../../hooks/fetchApi";
import useCustomToast from "../../../lib/useCustomToast";
import useScreenWidth from "../../../lib/useScreenWidth";
import { useTriggerRenderStore } from "../../../store/useTriggerRenderStore";
import { useDarkWhiteColor } from "../../../themes/colors";
import { axiosReseller } from "../../../utils/AxiosInstance";
import { splitCityName } from "../../../utils/helperFunction";

export const DetailSupplier = () => {
  const { id } = useParams();
  const [data, setData] = useState<DetailSupplierData | undefined>(undefined);
  const [dataProducts, setDataProducts] = useState<
    CardProductMyStoreInterface[] | undefined
  >(undefined);
  const [pagination, setPagination] = useState<PaginationInterface | undefined>(
    undefined
  );
  const [loaded, setLoaded] = useState<boolean>(false);
  const [search, setSearch] = useState<string>("");
  const [sort, setSort] = useState<string>("");
  const [page, setPage] = useState<number>(1);
  const [filter, setFilter] = useState<SelectOption | undefined>(undefined);
  const sw = useScreenWidth();
  const bgBnw = useDarkWhiteColor();
  const toast = useCustomToast();
  const navigate = useNavigate();
  const { isFetchLoading, addProduct } = useAddProduct();
  const { statusData, setStatusData } = useTriggerRenderStore();

  useEffect(() => {
    const categoryId = filter?.id ? filter?.id : "";

    axiosReseller
      .get(
        `/v1/reseller/detail-supplier/${id}?page=${page}&sort=${sort}&search=${search}&categoryId=${categoryId}`
      )
      .then((res) => {
        setData(res.data.data);
        setPagination(res.data.paginator);
      })
      .catch((err) => {
        toast({
          description: err?.response?.data?.message || "",
        });
      })
      .finally(() => {
        setLoaded(true);
      });
  }, [filter?.id, id, page, search, sort, statusData]);

  useEffect(() => {
    if (data) {
      const formattedData = data.products.map((item: ProductInterface) => ({
        id: item.id,
        name: item.name || "",
        image: item.main_picture || "",
        price: item.range_harga || "",
        category: item.category || "",
        added: item.added,
      }));

      setDataProducts(formattedData);
    }
  }, [data]);

  const goToDetailProduct = (id: any) => {
    navigate(`/reseller/search/product/${id}`);
  };

  const addProductToStore = (id: any) => {
    addProduct(id);
  };

  return (
    <VStack
      className="detail-supplier-page"
      w={"100%"}
      minH={"calc(100dvh - 60px)"}
      p={4}
      bg={bgBnw}
      align={"stretch"}
    >
      <TitlePageDashboard title="Detail Supplier" isSubPage={true} />

      <Stack
        className="wrap-header"
        w={"100%"}
        gap={8}
        mt={4}
        justify={"space-between"}
        direction={sw > 992 ? "row" : "column"}
      >
        <HStack className="wrap-profile" gap={4} flex={1}>
          <Avatar
            name={data?.supplier.name || ""}
            src={
              data?.supplier.logo
                ? `${process.env.REACT_APP_MEDIA_URL}${data?.supplier.logo}`
                : ""
            }
            size={"lg"}
          />

          <VStack gap={1} align={"stretch"}>
            <Text
              textStyle={"h-reg-title"}
              maxW={data?.bestSeller ? "600px" : undefined}
              textOverflow={"ellipsis"}
            >
              {data?.supplier.name || "Supplier BisnisLink"}
            </Text>

            {data?.supplier.city && (
              <HStack gap={1}>
                <Icon
                  as={RiMapPin2Line}
                  fontSize={[10, null, 12]}
                  opacity={0.6}
                />

                <Text
                  fontSize={[10, null, 12]}
                  variant={"secondary"}
                  noOfLines={1}
                  textOverflow={"ellipsis"}
                >
                  {splitCityName(data?.supplier.city)}
                </Text>
              </HStack>
            )}
          </VStack>
        </HStack>

        {loaded && data?.bestSeller && data.bestSeller.length > 0 && (
          <VStack borderRadius={"md"} align={"stretch"} flex={1}>
            <Text textStyle={"h-reg-title"}>Produk Terlaris</Text>

            <HStack w={"fit-content"} gap={2}>
              {data?.bestSeller.map((item) => (
                <BestSellerCard
                  key={item.product_id}
                  data={item}
                  flex={sw > 768 ? undefined : 1}
                />
              ))}
            </HStack>
          </VStack>
        )}
      </Stack>

      <HStack
        className="layout-list-product"
        w={"100%"}
        mt={4}
        alignItems={"start"}
      >
        {sw > 768 && (
          <Suspense fallback={<CardSkeleton row={1} height={435} />}>
            <CategoryFilter
              onSelected={(selectedFilter) => {
                setFilter(selectedFilter);
              }}
              onResetFilter={(reset) => {
                setFilter(undefined);
              }}
            />
          </Suspense>
        )}

        <VStack
          className="layout-product-supplier"
          w={"100%"}
          px={sw > 768 ? 4 : 0}
          pb={sw > 768 ? 4 : 0}
          align={"stretch"}
        >
          <HStack w={"100%"} justify={"space-between"}>
            <SearchInput
              onConfirm={(input) => {
                setSearch(input);
              }}
            />

            <HStack gap={[1, 2, 4]}>
              {sw < 768 && (
                <Suspense fallback={<CardSkeleton row={1} height={40} />}>
                  <FilterDrawer
                    onSelected={(select) => {
                      setFilter(select);
                    }}
                    onResetFilter={(reset) => {
                      setFilter(undefined);
                    }}
                  />
                </Suspense>
              )}

              <MenuSort
                onSelected={(selected) => {
                  setSort(`${selected?.note}`);
                }}
                children={undefined}
              />
            </HStack>
          </HStack>

          {filter && (
            <HStack gap={1}>
              <Text fontSize={[10, null, 12]}>
                Menampilkan pencarian untuk filter
              </Text>
              <Text fontSize={[10, null, 12]} fontWeight={"semibold"}>
                {`${filter?.name}`}
              </Text>
            </HStack>
          )}

          {!loaded ? (
            <CardSkeleton row={3} column={2} height={300} mt={4} />
          ) : (
            <VStack className="wrap-product" w={"1005"}>
              {dataProducts && dataProducts.length > 0 ? (
                <Grid
                  className="grid-product"
                  w={"100%"}
                  templateColumns={"repeat(auto-fill, minmax(160px, 1fr))"}
                  gap={4}
                  mt={4}
                >
                  {dataProducts &&
                    dataProducts.map((item) => (
                      <CardProductMyStore
                        btnText="Tambahkan"
                        key={item.id}
                        data={item}
                        btnIcon={RiAddLine}
                        isDisabled={isFetchLoading ? true : false}
                        onClickItem={(id) => {
                          goToDetailProduct(id);
                        }}
                        onClickButton={(id) => {
                          addProductToStore(id);
                          setStatusData();
                        }}
                      />
                    ))}
                </Grid>
              ) : (
                <Empty />
              )}
            </VStack>
          )}

          {loaded && pagination && dataProducts && dataProducts.length > 0 && (
            <Pagination
              mt={4}
              totalItems={pagination.total}
              pageSize={pagination.per_page}
              onPageChange={(page) => {
                setPage(page);
              }}
            />
          )}
        </VStack>
      </HStack>
    </VStack>
  );
};
